import React, { useState, useEffect } from "react";
import { ustawTryb } from '../utils/theme';
import { AlertIcon } from '../ui/icons';
import { IconButton, IconBadge, panelTopHairline } from '../ui/common';
import { ArrowLeftIcon } from '../ui/icons';
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { useTranslation, WYBIERALNE_JEZYKI } from "../translations";
import { API_BASE } from '../config';
import { scrollToTop } from '../utils/scrollToTop';

// SVG Icons
const SettingsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const PaletteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/>
    <circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/>
    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/>
    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/>
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const GlobeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 32px);
  background: var(--panel-bg);                 /* było: jasny gradient */
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-color);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  position: relative;
  overflow: hidden;
  min-height: calc(100vh - 48px);
  
  ${panelTopHairline}

  @media (min-width: 1200px) {
    max-width: 1100px;
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
    min-height: auto;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const TitleSection = styled.div`
  text-align: center;
  flex: 1;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2rem;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.025em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  /* Lepsza widoczność w dark mode */
  [data-theme='dark'] & {
    background: none;
    background-clip: initial;
    -webkit-background-clip: initial;
    -webkit-text-fill-color: var(--text);
    color: var(--text);
  }
`;

const Panel = styled.section`
  background: var(--card-bg);                  /* było: rgba(255,255,255,0.95) */
  backdrop-filter: blur(10px);
  border: 1px solid var(--input-border);      /* było: rgba(226,232,240,0.8) */
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${slideIn} 0.6s ease-out;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px var(--shadow-color);
    border-color: var(--input-border);
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--input-border);
`;

// Wspólna plakietka ikony z ui/common.js (IconBadge) — wołający przekazuje `$tone="steel"` itp.
// zamiast `bg="var(--primary)"`, patrz komentarz przy ICON_TONES w ui/common.js.
const PanelIcon = IconBadge;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text);
`;

const OptionGroup = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const OptionLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 12px;
`;

const OptionGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
`;

const OptionButton = styled.button`
  background: ${props => props.active ? 'var(--primary)' : 'var(--input-bg)'};
  color: ${props => props.active ? 'var(--primary-contrast)' : 'var(--text)'};
  border: 2px solid ${props => props.active ? 'var(--primary)' : 'var(--input-border)'};
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-1px);
    ${props => !props.active && `
      background: var(--table-row-hover);
      color: var(--primary);
      border-color: var(--primary);
    `}
  }
`;

const ThemePreview = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: ${props => props.bg};
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: var(--shadow-sm);
`;

const Message = styled.div`
  margin-bottom: 20px;
  padding: 14px 18px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  background: ${props => props.error ? 'var(--danger-bg)' : 'var(--success-bg)'};
  color: ${props => props.error ? 'var(--danger)' : 'var(--success)'};
  border: 1px solid ${props => props.error ? 'var(--danger-border)' : 'var(--success-border)'};
  display: flex;
  align-items: center;
  gap: 10px;
  opacity: ${props => props.show === false ? 0 : 1};
  transform: translateY(${props => props.show === false ? -10 : 0}px);
  transition: all 0.3s ease;
`;

export default function Preferences() {
  const { t, currentLanguage, setLanguage } = useTranslation();
  const navigate = useNavigate();
  
  const [theme, setTheme] = useState('light');
  const [showSaved, setShowSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // używamy kluczy nazw zamiast hardkodowanych stringów
  // Kolory ponizej to SWIADOMY wyjatek od tokenow: to probka wygladu KAZDEGO motywu w
  // przelaczniku, wiec musza zostac STALE niezaleznie od aktywnego motywu — gdyby zamienic
  // je na np. var(--bg), wszystkie trzy probki pokazywalyby ten sam, aktualny kolor.
  const themes = [
    { id: 'light', nameKey: 'preferences.theme.light', bg: '#f8fafc' },
    { id: 'dark', nameKey: 'preferences.theme.dark', bg: '#0f172a' },
    { id: 'system', nameKey: 'preferences.theme.system', bg: '#1e293b' }
  ];

  const languages = [
    // Kod jezyka zamiast flagi. Flagi to emotki (wbrew zasadzie „ikony, nie emoji" z V.2), a poza
    // tym myla: jezyk to nie panstwo — angielski pod flaga USA jest po prostu nietrafiony.
    { id: 'pl', nameKey: 'preferences.language.polish', kod: 'PL' },
    { id: 'en', nameKey: 'preferences.language.english', kod: 'EN' },
    { id: 'de', nameKey: 'preferences.language.german', kod: 'DE' }
  ].filter(lang => WYBIERALNE_JEZYKI.includes(lang.id));

  // Ładowanie preferencji przy starcie
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        // Fallback na localStorage jeśli nie ma userId
        const savedTheme = localStorage.getItem('theme') || 'light';
        setTheme(savedTheme);
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE}/api/preferences?userId=${userId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const prefs = await response.json();
        setTheme(prefs.theme || 'light');
        
        // Zastosuj motyw od razu
        applyTheme(prefs.theme || 'light');
      } else {
        // Fallback na localStorage
        const savedTheme = localStorage.getItem('theme') || 'light';
        setTheme(savedTheme);
        applyTheme(savedTheme);
      }
    } catch (error) {
      console.error('Błąd ładowania preferencji:', error);
      // Fallback na localStorage
      const savedTheme = localStorage.getItem('theme') || 'light';
      setTheme(savedTheme);
      applyTheme(savedTheme);
    } finally {
      setLoading(false);
    }
  };


  // Wspolna funkcja zamiast wlasnej kopii. Poprzednia tylko ustawiala atrybut, wiec tryb
  // „systemowy" nie reagowal na zmiane ustawien systemu do czasu przeladowania strony, a zapis
  // szedl pod innym kluczem niz ten, ktory czytamy przy starcie.
  const applyTheme = (themeValue) => ustawTryb(themeValue);

  const handleThemeChange = async (newTheme) => {
    setTheme(newTheme);
    setError('');
    setSuccess('');
    
    // ustawTryb sam zapisuje wybor pod kluczem `theme`, wiec osobny zapis bylby dubletem.
    applyTheme(newTheme);
    
    // Zapisz w bazie danych
    await savePreferences(newTheme, currentLanguage);
    
    showSavedMessage();
  };

  const handleLanguageChange = async (newLanguage) => {
    setError('');
    setSuccess('');
    setLanguage(newLanguage);
    await savePreferences(theme, newLanguage);
    showSavedMessage();
  };

  const savePreferences = async (themeValue, languageValue) => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) return;

      const csrf = localStorage.getItem('csrfToken') || '';
      const response = await fetch(`${API_BASE}/api/preferences`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf
        },
        body: JSON.stringify({
          theme: themeValue,
          language: languageValue
        })
      });

      if (!response.ok) {
        setError(t('preferences.saveError') || 'Nie udało się zapisać preferencji.');
        scrollToTop();
        return;
      }
    } catch (error) {
      setError(t('preferences.saveError') || 'Nie udało się zapisać preferencji.');
      scrollToTop();
    }
  };

  const showSavedMessage = () => {
    setSuccess(t('preferences.saved'));
    setShowSaved(true);
    scrollToTop();
    setTimeout(() => {
      setShowSaved(false);
      setSuccess('');
    }, 2000);
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div>{t('preferences.loading')}</div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
          <ArrowLeftIcon />
        </IconButton>
        <TitleSection>
          <Title>
            <SettingsIcon />
            {t('profile.preferences')}
          </Title>
        </TitleSection>
      </Header>

      {error && (
        <Message error>
          <AlertIcon />
          {error}
        </Message>
      )}

      {success && (
        <Message show={showSaved}>
          <CheckIcon />
          {success}
        </Message>
      )}

      <Panel>
        <PanelHeader>
          <PanelIcon $tone="dusk">
            <PaletteIcon />
          </PanelIcon>
          <PanelTitle>{t('preferences.theme')}</PanelTitle>
        </PanelHeader>
        
        <OptionGroup>
          <OptionLabel>{t('preferences.chooseTheme')}</OptionLabel>
          <OptionGrid>
            {themes.map(themeOption => (
              <OptionButton
                key={themeOption.id}
                active={theme === themeOption.id}
                onClick={() => handleThemeChange(themeOption.id)}
              >
                <ThemePreview bg={themeOption.bg} />
                {t(themeOption.nameKey)}
              </OptionButton>
            ))}
          </OptionGrid>
        </OptionGroup>
      </Panel>

      {/* P8.1: wybór języka tylko, gdy jest z czego wybierać (translations.js#WYBIERALNE_JEZYKI). */}
      {languages.length > 1 && (
      <Panel>
        <PanelHeader>
          <PanelIcon $tone="pine">
            <GlobeIcon />
          </PanelIcon>
          <PanelTitle>{t('preferences.language')}</PanelTitle>
        </PanelHeader>
        
        <OptionGroup>
          <OptionLabel>{t('preferences.chooseLanguage')}</OptionLabel>
          <OptionGrid>
            {languages.map(lang => (
              <OptionButton
                key={lang.id}
                active={currentLanguage === lang.id}
                onClick={() => handleLanguageChange(lang.id)}
              >
                <span style={{
                  fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.04em',
                  padding: '3px 7px', borderRadius: 6, minWidth: 30, textAlign: 'center',
                  background: 'var(--table-header-bg)', color: 'var(--text-muted)',
                }}>{lang.kod}</span>
                {t(lang.nameKey)}
              </OptionButton>
            ))}
          </OptionGrid>
        </OptionGroup>
      </Panel>
      )}
    </Container>
  );
}